import React from 'react';
import {
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Box,
} from '@mui/material';

interface InsuranceFormFieldsProps {
  formData: {
    policyNumber: string;
    insurerName: string;
    policyName: string;
    effectiveDate: string;
    expiryDate: string;
    active: boolean;
    liabilityLimit: string;
    collisionDeductible: string;
    comprehensiveDeductible: string;
    allPerilsDeductible: string;
    notes: string;
  };
  handleChange: (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const InsuranceFormFields = ({ formData, handleChange }: InsuranceFormFieldsProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
    {/* Policy Information Section */}
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
        Policy Information
      </Typography>
      <Divider />
    </Box>

    <TextField
      fullWidth
      label="Policy Number"
      required
      value={formData.policyNumber}
      onChange={handleChange('policyNumber')}
      helperText="Unique policy identifier"
    />

    <TextField
      fullWidth
      label="Insurance Company"
      required
      value={formData.insurerName}
      onChange={handleChange('insurerName')}
      helperText="Name of the insurance provider"
    />

    <TextField
      fullWidth
      label="Policy Name"
      value={formData.policyName}
      onChange={handleChange('policyName')}
      helperText="Optional friendly name for this policy"
    />

    {/* Coverage Period Section */}
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
        Coverage Period
      </Typography>
      <Divider />
    </Box>

    <TextField
      fullWidth
      label="Effective Date"
      type="date"
      required
      value={formData.effectiveDate}
      onChange={handleChange('effectiveDate')}
      InputLabelProps={{ shrink: true }}
    />

    <TextField
      fullWidth
      label="Expiry Date"
      type="date"
      required
      value={formData.expiryDate}
      onChange={handleChange('expiryDate')}
      InputLabelProps={{ shrink: true }}
    />

    <FormControlLabel
      control={
        <Switch
          checked={formData.active}
          onChange={handleChange('active')}
        />
      }
      label="Active"
    />

    {/* Coverage Details Section */}
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
        Coverage Details
      </Typography>
      <Divider />
    </Box>

    <TextField
      fullWidth
      label="Liability Limit"
      type="number"
      value={formData.liabilityLimit}
      onChange={handleChange('liabilityLimit')}
      helperText="Maximum liability coverage in dollars"
    />

    <TextField
      fullWidth
      label="Collision Deductible"
      type="number"
      value={formData.collisionDeductible}
      onChange={handleChange('collisionDeductible')}
      helperText="Deductible for collision coverage"
    />

    <TextField
      fullWidth
      label="Comprehensive Deductible"
      type="number"
      value={formData.comprehensiveDeductible}
      onChange={handleChange('comprehensiveDeductible')}
      helperText="Deductible for comprehensive coverage"
    />

    <TextField
      fullWidth
      label="All Perils Deductible"
      type="number"
      value={formData.allPerilsDeductible}
      onChange={handleChange('allPerilsDeductible')}
      helperText="Combined collision + comprehensive + extra coverage"
    />

    {/* Additional Information Section */}
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
        Additional Information
      </Typography>
      <Divider />
    </Box>

    <TextField
      fullWidth
      label="Notes"
      multiline
      rows={4}
      value={formData.notes}
      onChange={handleChange('notes')}
      helperText="Additional notes about this policy"
    />
  </Box>
);
